import { useAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, useNavigate, useParams } from 'react-router-dom';

import { Button, Card, CardSection, Flex, IconSvg, Text } from '@/lib/v2/components';
import { RenameItem } from '@/lib/v2/examples';
import { useRenameModal } from '@/lib/v2/examples/RenameItem/hooks/useRenameModal';
import { SectionHeader } from '@/lib/v2/examples/SectionHeader';
import { EditPencilIcon } from '@/lib/v2/icons/outline';
import { CampaignsIcon } from '@/lib/v2/icons/solid';

import { EventListenerType } from '@/src/application/hooks/enums/EventListenerEnum';
import { SendActionsPostMessage } from '@/src/compat/sendActionsPostMessage';
import useMessage from '@/src/compat/useMessage';
import { atomInfoCampaignV1 } from '@/src/modules/CampaignsModule/atoms/campaignAtom';
import { CAMPAIGNS_PATH } from '@/src/modules/CampaignsModule/constants';
import { useCampaignActions } from '@/src/modules/CampaignsModule/hooks/useCampaignActions';
import { ActionTypeName } from '@/src/modules/CampaignsModule/interfaces/Campaigns';

export type OutletContextActionsLayoutProps = {
  showSpinnerByAction: boolean;
};

const ActionsLayout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { campaignId, actionId, actionType } = useParams();

  const isActionsPath = useMemo(() => {
    const validRoutes: ActionTypeName[] = [
      'email',
      'email-attachment',
      'email-trigger',
      'mail-api',
      'sms',
      'sms-api',
    ];

    return validRoutes.some((route) => route === actionType);
  }, [actionType]);

  const [showSpinnerByAction, setShowSpinnerByAction] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);
  const [infoCampaignV1, setInfoCampaignV1] = useAtom(atomInfoCampaignV1);

  const { editAction, loading } = useCampaignActions({ campaignID: Number(campaignId) });

  const { position, editNameModalIsOpen, setEditNameModalIsOpen, handleOpenEditNameModal } =
    useRenameModal();

  const haveData = useMemo(() => infoCampaignV1?.campaign?.name, [infoCampaignV1]);

  //TO DO: use editAction function, error with context
  const handleApplyRename = useCallback(
    async (nameValue: string) => {
      const statusOK = await editAction({ name: nameValue, actionId: Number(actionId) });

      if (statusOK) {
        setInfoCampaignV1({
          ...infoCampaignV1,
          action: {
            id: Number(actionId),
            name: nameValue,
          },
        });
      }
      setEditNameModalIsOpen(false);
    },
    [actionId, editAction, infoCampaignV1, setEditNameModalIsOpen, setInfoCampaignV1]
  );

  useMessage(EventListenerType.Loaded, (_) => {
    showSpinnerByAction && setIsLoaded(true);
  });

  useEffect(() => {
    const type = actionType;
    isLoaded &&
      showSpinnerByAction &&
      SendActionsPostMessage('Campaign', 'ViewCampaign', { id: actionId, type });
  }, [showSpinnerByAction, isLoaded, actionId, actionType]);

  useMessage(EventListenerType.LoadedCampaign, (_, payload) => {
    if (isLoaded && showSpinnerByAction) {
      if (!haveData) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        // cspell:disable
        setInfoCampaignV1({
          campaign: {
            id: parseInt(campaignId || '0'),
            name: payload.campaniaNombre,
          },
          action: {
            id: Number(actionId),
            name: payload.nombre,
          },
        });
      }
      setShowSpinnerByAction(false);
    }
  });

  useEffect(() => {
    SendActionsPostMessage('Campaign', 'VerifyLoaded');
  }, []);

  if (!isActionsPath) {
    return <Navigate to={CAMPAIGNS_PATH} />;
  }

  const [right, top] = position;

  return (
    <Card>
      <CardSection noPadding>
        <Flex column>
          <SectionHeader
            backButton
            sticky
            icon={<CampaignsIcon />}
            isLoading={showSpinnerByAction}
            titleNode={
              <Text as="h3" className="!2xl:text-20 !text-16" fontWeight="medium" variant="title-1">
                <Flex withGap>
                  {infoCampaignV1?.campaign?.name}
                  {' / '}
                  <Text color="primary">{infoCampaignV1?.action?.name}</Text>
                  <Button
                    link
                    className="!h-auto !p-0"
                    iconLeft={
                      <IconSvg height="16px" svgComponent={<EditPencilIcon />} width="16px" />
                    }
                    id="back-to-campaigns-button"
                    onClick={handleOpenEditNameModal}
                  />
                </Flex>
              </Text>
            }
            onBack={() => navigate && navigate(`/v2/campaigns/${infoCampaignV1.campaign.id}`)}
          />
          <Outlet context={{ showSpinnerByAction } as OutletContextActionsLayoutProps} />
        </Flex>
      </CardSection>
      <RenameItem
        actionButtonText={t('ITEMS_FILTERS.apply')}
        isLoading={loading}
        isOpen={editNameModalIsOpen}
        placeholder={t('CAMPAIGNS_ACTIONS.renameAction')}
        rightClick={right}
        title={t('ITEMS_FILTERS.rename')}
        topClick={top}
        value={infoCampaignV1?.action?.name}
        onActionButton={handleApplyRename}
        onClose={() => setEditNameModalIsOpen(false)}
      />
    </Card>
  );
};

export default ActionsLayout;
